import React, { Fragment, useState } from "react";
import style from "../../../scss/Registro.module.scss";
import { crearElectores, validarCedula } from "../../../Api/ApiMetodo";
import Swal from "sweetalert2";
import useCatalogos from "../../../hooks/useCatalogos";
import SelectComponent from "../../../components/SelectComponent";
import SelectLiderComponent from "../../../components/SelectLiderComponent";
import { obtenerMensajeError } from "../../../utils/mensajeError";
import { encolar, esErrorDeRed, listarPendientesUsuario } from "../../../utils/colaSincronizacion";
import { MAX_COMPROMISO } from "../../../constants/compromiso";
import { fechaHoyTexto } from "../../../utils/agenda";

const FORMULARIO_VACIO = {
  nombres: "",
  apellidos: "",
  direccion: "",
  lugar: "",
  mesa: "",
  telefono: "",
  lider: "",
  compromiso: "",
  compromisoFecha: "",
};

// Estados de la validación de la cédula
const VALIDACION = {
  PENDIENTE: "pendiente",
  VALIDANDO: "validando",
  DISPONIBLE: "disponible",
  REGISTRADO: "registrado",
  // sin internet no se puede consultar: se deja registrar y el servidor
  // valida la cédula al sincronizar
  SIN_CONEXION: "sin-conexion",
  PENDIENTE_LOCAL: "pendiente-local",
};

const RegistroComponent = () => {
  const { opcionesLugar, opcionesMesaDe } = useCatalogos({ incluirCandidatos: false });
  const [cedula, setCedula] = useState("");
  const [validacion, setValidacion] = useState(VALIDACION.PENDIENTE);
  const [electorExistente, setElectorExistente] = useState(null);
  const [formData, setFormData] = useState(FORMULARIO_VACIO);
  const [loading, setLoading] = useState(false);

  // los demás campos solo se habilitan cuando la cédula ya se validó y está libre
  const camposHabilitados = validacion === VALIDACION.DISPONIBLE || validacion === VALIDACION.SIN_CONEXION;

  const handleCedulaChange = (e) => {
    // al cambiar la cédula hay que volver a validarla
    setCedula(e.target.value.replace(/\D/g, ""));
    setValidacion(VALIDACION.PENDIENTE);
    setElectorExistente(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // SelectComponent entrega solo el value (null al limpiar)
  const cambiarSelect = (campo) => (valor) => setFormData({ ...formData, [campo]: valor ?? "" });

  // las mesas dependen del lugar: al cambiarlo se vuelve a elegir la mesa
  const cambiarLugar = (valor) => setFormData({ ...formData, lugar: valor ?? "", mesa: "" });

  const handleValidarCedula = async () => {
    if (!cedula) return;
    setValidacion(VALIDACION.VALIDANDO);
    try {
      const respuesta = await validarCedula(cedula);
      if (respuesta.registrado) {
        setElectorExistente(respuesta.elector);
        setValidacion(VALIDACION.REGISTRADO);
      } else {
        setValidacion(VALIDACION.DISPONIBLE);
      }
    } catch (error) {
      if (esErrorDeRed(error)) {
        // sin señal: se revisa al menos que no esté ya guardada en este equipo
        const pendientes = await listarPendientesUsuario();
        const yaGuardada = pendientes.some((p) => p.tipo === "elector" && p.datos.cedula === cedula);
        setValidacion(yaGuardada ? VALIDACION.PENDIENTE_LOCAL : VALIDACION.SIN_CONEXION);
        return;
      }
      setValidacion(VALIDACION.PENDIENTE);
      Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
    }
  };

  const limpiarTodo = () => {
    setCedula("");
    setFormData(FORMULARIO_VACIO);
    setValidacion(VALIDACION.PENDIENTE);
    setElectorExistente(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Enter en el campo de cédula valida en vez de registrar
    if (!camposHabilitados) {
      handleValidarCedula();
      return;
    }

    setLoading(true);
    const elector = { ...formData, cedula };
    try {
      const response = await crearElectores(elector);
      Swal.fire({
        icon: "success",
        title: "¡Éxito!",
        text: response.msg || "Elector registrado correctamente",
      });
      limpiarTodo();
    } catch (error) {
      if (esErrorDeRed(error)) {
        // sin señal: queda guardado en el equipo y se envía al volver la conexión
        await encolar("elector", { datos: elector });
        Swal.fire({
          icon: "info",
          title: "Guardado sin conexión",
          text: "El elector quedó guardado en este equipo y se enviará automáticamente cuando vuelva la señal.",
        });
        limpiarTodo();
        return;
      }
      Swal.fire({
        icon: "error",
        title: "No se pudo registrar",
        text: obtenerMensajeError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <div className={style.Registro}>
        <form onSubmit={handleSubmit} className={style.formulario}>
          <h2 className="text-center">REGISTRO VOTANTES</h2>

          {/* Paso 1: validar la cédula */}
          <div className="row gy-2 align-items-end">
            <div className="col-12 col-md-8">
              <label htmlFor="cedula">* Numero de cedula:</label>
              <input
                type="text"
                inputMode="numeric"
                id="cedula"
                name="cedula"
                className="form-control"
                value={cedula}
                onChange={handleCedulaChange}
                placeholder="Ingresa la cédula y valídala"
                required
              />
            </div>
            <div className="col-12 col-md-4">
              <button
                type="button"
                className={`btn btn-outline-primary w-100 ${style.botonValidar}`}
                onClick={handleValidarCedula}
                disabled={!cedula || validacion === VALIDACION.VALIDANDO}
              >
                {validacion === VALIDACION.VALIDANDO ? "Validando..." : "Validar cédula"}
              </button>
            </div>
          </div>

          {validacion === VALIDACION.PENDIENTE && (
            <p className={style.ayuda}>
              Primero valida la cédula. Los demás campos se habilitan si no está registrada.
            </p>
          )}

          {validacion === VALIDACION.SIN_CONEXION && (
            <div className="alert alert-info mt-3 mb-0" role="status">
              <strong>Sin conexión:</strong> no se pudo verificar la cédula <strong>{cedula}</strong>. Puedes
              registrarla; se guardará en este equipo y el servidor la validará al volver la señal.
            </div>
          )}

          {validacion === VALIDACION.PENDIENTE_LOCAL && (
            <div className="alert alert-warning mt-3 mb-0" role="alert">
              La cédula <strong>{cedula}</strong> ya está guardada en este equipo esperando conexión para enviarse.
            </div>
          )}

          {validacion === VALIDACION.DISPONIBLE && (
            <div className="alert alert-success mt-3 mb-0" role="status">
              La cédula <strong>{cedula}</strong> no está registrada. Completa los datos del elector.
            </div>
          )}

          {validacion === VALIDACION.REGISTRADO && electorExistente && (
            <div className={`alert alert-warning mt-3 mb-0 ${style.electorExistente}`} role="alert">
              <p className={style.tituloExistente}>El elector se encuentra registrado</p>
              <dl className={style.datosExistente}>
                <dt>Cédula</dt>
                <dd>{electorExistente.cedula}</dd>
                <dt>Nombre</dt>
                <dd>
                  {electorExistente.nombres} {electorExistente.apellidos}
                </dd>
                <dt>Líder</dt>
                <dd>{electorExistente.lider || "Sin líder asignado"}</dd>
                <dt>Lugar / Mesa</dt>
                <dd>
                  {electorExistente.lugar || "Sin lugar"} / {electorExistente.mesa ? `Mesa ${electorExistente.mesa}` : "Sin mesa"}
                </dd>
                <dt>Registrado por</dt>
                <dd>
                  {electorExistente.registradoPorMi ? "Ti" : electorExistente.registradoPor || "No disponible"}
                </dd>
              </dl>
            </div>
          )}

          {/* Paso 2: datos del elector (deshabilitados hasta validar la cédula) */}
          <fieldset disabled={!camposHabilitados} className={style.campos}>
            <div className="row gy-4">
              <div className="col-12 col-md-6">
                <label htmlFor="nombres">* Nombres:</label>
                <input
                  type="text"
                  id="nombres"
                  name="nombres"
                  className="form-control"
                  value={formData.nombres}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-12 col-md-6 ">
                <label htmlFor="apellidos">* Apellidos:</label>
                <input
                  type="text"
                  id="apellidos"
                  name="apellidos"
                  className="form-control"
                  value={formData.apellidos}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-12 col-md-6">
                <SelectComponent
                  id="lugar"
                  name="lugar"
                  etiqueta="Lugar"
                  opciones={opcionesLugar}
                  valor={formData.lugar}
                  onCambiar={cambiarLugar}
                  placeholder="Seleccionar lugar"
                />
              </div>
              <div className="col-12 col-md-6">
                <SelectComponent
                  id="mesa"
                  name="mesa"
                  etiqueta="Mesa"
                  opciones={opcionesMesaDe(formData.lugar)}
                  valor={formData.mesa}
                  onCambiar={cambiarSelect("mesa")}
                  deshabilitado={!formData.lugar}
                  placeholder={formData.lugar ? "Seleccionar mesa" : "Primero elige el lugar"}
                />
              </div>
              <div className="col-12 col-md-6 ">
                <label htmlFor="direccion">Direccion:</label>
                <input
                  type="text"
                  id="direccion"
                  name="direccion"
                  className="form-control"
                  value={formData.direccion}
                  onChange={handleChange}
                />
              </div>
              <div className="col-12 col-md-6 ">
                <label htmlFor="telefono">Telefono:</label>
                <input
                  type="text"
                  id="telefono"
                  name="telefono"
                  className="form-control"
                  value={formData.telefono}
                  onChange={handleChange}
                />
              </div>
              <div className="col-12 col-md-6 ">
                {/* lista de líderes existentes: evita el mismo líder escrito distinto */}
                <SelectLiderComponent
                  etiqueta="Líder"
                  valor={formData.lider}
                  onCambiar={(lider) => setFormData({ ...formData, lider })}
                />
              </div>
              <div className="col-12">
                <label htmlFor="compromiso">Compromiso:</label>
                <textarea
                  id="compromiso"
                  name="compromiso"
                  className="form-control"
                  rows={3}
                  maxLength={MAX_COMPROMISO}
                  value={formData.compromiso}
                  onChange={handleChange}
                  placeholder="Describe el compromiso adquirido con el votante (opcional)"
                />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="compromisoFecha">Fecha del compromiso (opcional):</label>
                <input
                  type="date"
                  id="compromisoFecha"
                  name="compromisoFecha"
                  className="form-control"
                  min={fechaHoyTexto()}
                  value={formData.compromisoFecha}
                  onChange={handleChange}
                  disabled={!formData.compromiso.trim()}
                  aria-describedby="ayuda-compromiso-fecha"
                />
                <small id="ayuda-compromiso-fecha" className={style.ayudaCampo}>
                  {formData.compromiso.trim()
                    ? "Con fecha, el compromiso aparece en tu agenda y te avisamos antes."
                    : "Escribe primero el compromiso."}
                </small>
              </div>
            </div>
            <div className="py-4">
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={loading}
              >
                {loading ? "Guardando..." : "Registrar Elector"}
              </button>
            </div>
          </fieldset>
        </form>
      </div>
    </Fragment>
  );
};
export default RegistroComponent;
